"use client";

import type { SwitchProps as RadixSwitchProps } from "@radix-ui/react-switch";
import * as RadixSwitch from "@radix-ui/react-switch";
import clsx from "clsx";
import { forwardRef } from "react";

type SwitchProps = {
  label?: string;
  description?: string;
} & Omit<RadixSwitchProps, "asChild">;

const container = {
  base: "inline-flex items-start select-none gap-2",
  switch:
    "cursor-pointer relative inline-flex h-6 w-10 rounded-full items-center px-0.5 transition-colors transition-shadows focus:outline-none focus-visible:ring-2 ring-offset-2 ring-theme-accent group data-[state=checked]:bg-theme-accent data-[state=unchecked]:bg-gray-500 disabled:opacity-50 disabled:pointer-events-none",
  thumb:
    "block h-5 w-5 rounded-full bg-white shadow transform transition-transform data-[state=checked]:translate-x-4 group-active:scale-90",
};

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      checked,
      onCheckedChange,
      disabled,
      required,
      name,
      id,
      value,
      label,
      description,
      defaultChecked,
    },
    ref,
  ) => {
    return (
      <div className={container.base}>
        <RadixSwitch.Root
          checked={checked}
          defaultChecked={defaultChecked}
          onCheckedChange={onCheckedChange}
          disabled={disabled}
          required={required}
          name={name}
          id={id ?? name}
          value={value}
          ref={ref}
          className={clsx(container.switch)}
        >
          <RadixSwitch.Thumb className={container.thumb} />
        </RadixSwitch.Root>
        {label && (
          <label className="flex flex-col flex-1" htmlFor={id ?? name}>
            <div className="text-gray-1200 text-base font-medium">{label}</div>
            {description && <div className="text-gray-1100">{description}</div>}
          </label>
        )}
      </div>
    );
  },
);

Switch.displayName = "Switch";
